/**
 * CoachRaterMini — Compact rating for the LLM coach explanation.
 *
 * Shows the coach advice text in a collapsed 1-2 line preview with:
 * - Star rating (1-5)
 * - "Misleading?" toggle
 * - Optional correction text
 */

import { useState, useCallback } from 'react';

// ============================================
// Types
// ============================================

export interface CoachRatingResult {
  coach_advice: string;
  rating: number;
  is_misleading: boolean;
  expert_correction?: string;
}

interface CoachRaterMiniProps {
  coachAdvice: string | null;
  onChange: (rating: CoachRatingResult | null) => void;
}

// ============================================
// Star rating
// ============================================

function StarRating({ value, onChange }: { value: number; onChange: (v: number) => void }) {
  return (
    <div className="coach-rater-stars">
      {[1, 2, 3, 4, 5].map(star => (
        <button
          key={star}
          className={`coach-rater-star ${star <= value ? 'active' : ''}`}
          onClick={() => onChange(star)}
          title={`${star}/5`}
        >
          ★
        </button>
      ))}
    </div>
  );
}

// ============================================
// CoachRaterMini component
// ============================================

export default function CoachRaterMini({ coachAdvice, onChange }: CoachRaterMiniProps) {
  const [rating, setRating] = useState(0);
  const [isMisleading, setIsMisleading] = useState(false);
  const [correction, setCorrection] = useState('');
  const [expanded, setExpanded] = useState(false);

  const emitChange = useCallback((r: number, misleading: boolean, corr: string) => {
    if (!coachAdvice || r === 0) {
      onChange(null);
      return;
    }
    onChange({
      coach_advice: coachAdvice,
      rating: r,
      is_misleading: misleading,
      expert_correction: corr || undefined,
    });
  }, [coachAdvice, onChange]);

  const handleRating = useCallback((v: number) => {
    setRating(v);
    emitChange(v, isMisleading, correction);
  }, [isMisleading, correction, emitChange]);

  const handleMisleading = useCallback(() => {
    const next = !isMisleading;
    setIsMisleading(next);
    emitChange(rating, next, correction);
  }, [isMisleading, rating, correction, emitChange]);

  const handleCorrection = useCallback((v: string) => {
    setCorrection(v);
    emitChange(rating, isMisleading, v);
  }, [rating, isMisleading, emitChange]);

  if (!coachAdvice) return null;

  return (
    <div className="coach-rater-mini">
      <h5 className="coach-rater-title">Évaluation du conseil</h5>

      <div
        className={`coach-rater-preview ${expanded ? 'expanded' : ''}`}
        onClick={() => setExpanded(prev => !prev)}
      >
        {coachAdvice}
      </div>

      <div className="coach-rater-controls">
        <StarRating value={rating} onChange={handleRating} />

        <label className="coach-rater-misleading">
          <input
            type="checkbox"
            checked={isMisleading}
            onChange={handleMisleading}
          />
          Trompeur ?
        </label>
      </div>

      {(isMisleading || rating > 0) && (
        <input
          className="coach-rater-correction"
          type="text"
          placeholder="Ce qu'il aurait fallu dire..."
          value={correction}
          onChange={e => handleCorrection(e.target.value)}
        />
      )}
    </div>
  );
}
